import apiClient from "../core/config/api";
import {
  HubspotBulkJob,
  HubspotBulkJobCreate,
  HubspotBulkJobsParams,
  HubspotConnectResponse,
  HubspotConnection,
  HubspotExecution,
  HubspotExecutionsParams,
  HubspotList,
  HubspotPropertySyncResponse,
} from "../features/integrations/types";

export const hubspotService = {
  // Returns the HubSpot authorize URL to send the browser to. The redirect is
  // the caller's job so the data layer stays free of navigation side effects.
  startConnect: async (): Promise<HubspotConnectResponse> => {
    const { data } = await apiClient.post("/hubspot/connect");
    return data;
  },

  // The backend answers 200 with a JSON null when the account has no portal
  // connected, so an empty body is "not connected", not an error.
  getConnection: async (): Promise<HubspotConnection | null> => {
    const { data } = await apiClient.get("/hubspot/connection");
    return data ? data : null;
  },

  disconnect: async (): Promise<void> => {
    await apiClient.delete("/hubspot/connection");
  },

  getExecutions: async (
    params: HubspotExecutionsParams = {}
  ): Promise<HubspotExecution[]> => {
    const { data } = await apiClient.get("/hubspot/executions", { params });
    return Array.isArray(data) ? data : [];
  },

  // Safe to call repeatedly - the backend treats an already-existing property
  // as success, which is what makes this the recovery path for a portal whose
  // properties were deleted by hand.
  syncProperties: async (): Promise<HubspotPropertySyncResponse> => {
    const { data } = await apiClient.post("/hubspot/properties/sync");
    return data;
  },

  // 403 here means the portal was connected before the read scopes existed and
  // has to reconnect - it does NOT mean the connection is broken. The caller
  // distinguishes the two off connection.can_verify_lists.
  getLists: async (search = ""): Promise<HubspotList[]> => {
    const { data } = await apiClient.get("/hubspot/lists", {
      params: search ? { search } : undefined,
    });
    return Array.isArray(data) ? data : [];
  },

  // Returns as soon as the job row exists; the work happens in the backend
  // worker, so this does not block on the size of the list.
  createBulkJob: async (payload: HubspotBulkJobCreate): Promise<HubspotBulkJob> => {
    const { data } = await apiClient.post("/hubspot/bulk-jobs", payload);
    return data;
  },

  getBulkJobs: async (
    params: HubspotBulkJobsParams = {}
  ): Promise<HubspotBulkJob[]> => {
    const { data } = await apiClient.get("/hubspot/bulk-jobs", { params });
    return Array.isArray(data) ? data : [];
  },
};
